import React, { useEffect, useRef, useState } from 'react';
import { AP_DISTRICTS } from './editions';
import { IconFirst, IconLast, IconLeft, IconRight } from './Icons';
import { playTurn } from './sound';
import { EditionMap } from './types';

interface Props {
  date: string;
  pageCount: number;
  page: number;
  editions: EditionMap;
  edition: string; // 'Main' or a district name
  isAdmin: boolean;
  onGo: (page: number, edition?: string) => void;
  onNoDistrict: (district: string) => void;
  onEditEditions: () => void;
  hrefFor?: (page: number) => string; // address behind the page links (default: the signed-in reader)
}

/** The pagination links and the Edition dropdown (Main / District > list of districts) that sit in the top bar. */
export function PageNav({ date, pageCount, page, editions, edition, isAdmin, onGo, onNoDistrict, onEditEditions, hrefFor }: Props) {
  const [open, setOpen] = useState(false);
  const [districts, setDistricts] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent | TouchEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) {
        setOpen(false);
        setDistricts(false);
      }
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setDistricts(false);
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setDistricts(false);
  };
  const turn = (n: number) => {
    if (n < 1 || n > pageCount || n === page) return;
    playTurn(n > page ? 'next' : 'prev', n);
    onGo(n);
  };

  const pickMain = () => {
    close();
    if (page !== 1 || edition !== 'Main') playTurn('prev', 1);
    onGo(1, 'Main');
  };
  const pickDistrict = (d: string) => {
    close();
    const p = editions[d];
    if (!p || p > pageCount) {
      onNoDistrict(d);
      return;
    }
    playTurn(p > page ? 'next' : 'prev', p);
    onGo(p, d);
  };

  const label = edition === 'Main' ? 'Edition' : 'Edition: ' + edition;

  const href = (n: number) => (hrefFor ? hrefFor(n) : `#/p/${date}/${n}`);
  const step = (n: number, off: boolean, cls: string, label: string, icon: React.ReactNode, text: string) => (
    <a
      className={'cbtn pn-step ' + cls + (off ? ' off' : '')}
      href={href(Math.min(Math.max(1, n), pageCount))}
      aria-disabled={off}
      tabIndex={off ? -1 : undefined}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        if (!off) turn(n);
      }}
    >
      {icon} <span>{text}</span>
    </a>
  );

  return (
    <nav className="pagenav" aria-label="Pages">
      {step(1, page <= 1, 'tone-indigo', 'First page', <IconFirst size={16} />, 'First')}
      {step(page - 1, page <= 1, 'tone-blue', 'Previous page', <IconLeft size={16} />, 'Previous')}
      <div className="pn-links">
        {[page - 1, page, page + 1]
          .filter((n) => n >= 1 && n <= pageCount)
          .map((n) => (
            <a
              key={n}
              className={'pn-link' + (n === page ? ' cur' : '')}
              href={href(n)}
              aria-current={n === page ? 'page' : undefined}
              aria-label={`Page ${n}`}
              onClick={(e) => {
                e.preventDefault();
                turn(n);
              }}
            >
              {n}
            </a>
          ))}
      </div>
      {step(page + 1, page >= pageCount, 'tone-teal', 'Next page', <IconRight size={16} />, 'Next')}
      {step(pageCount, page >= pageCount, 'tone-purple', 'Last page', <IconLast size={16} />, 'Last')}

      <div className="edition" ref={box}>
        <button
          className={'cbtn tone-amber pn-edition' + (open ? ' open' : '')}
          onClick={() => {
            setOpen((o) => !o);
            setDistricts(false);
          }}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {label} <span className="caret">▾</span>
        </button>
        {open && (
          <div className="ed-menus">
            <ul className="ed-menu" role="menu">
              <li>
                <button role="menuitem" className={edition === 'Main' ? 'on' : ''} onClick={pickMain}>
                  Main
                </button>
              </li>
              <li>
                <button role="menuitem" className={'has-sub' + (edition !== 'Main' || districts ? ' on' : '')} onClick={() => setDistricts((d) => !d)} aria-expanded={districts}>
                  District <span className="caret">▸</span>
                </button>
              </li>
              {isAdmin && (
                <li className="ed-admin">
                  <button
                    role="menuitem"
                    onClick={() => {
                      close();
                      onEditEditions();
                    }}
                  >
                    Set district pages…
                  </button>
                </li>
              )}
            </ul>
            {districts && (
              <ul className="ed-menu ed-districts" role="menu" aria-label="Districts of Andhra Pradesh">
                <li className="ed-title">Andhra Pradesh</li>
                {AP_DISTRICTS.map((d) => (
                  <li key={d}>
                    <button role="menuitem" className={edition === d ? 'on' : ''} onClick={() => pickDistrict(d)}>
                      {d}
                      {editions[d] ? <span className="ed-page">p.{editions[d]}</span> : <span className="ed-page none">–</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
